//External Libraries
import { faThumbtack as farThumbtack } from '@fortawesome/pro-regular-svg-icons/faThumbtack';
import { useTranslation } from 'react-i18next';

//Utils
import { formatAnnouncementDate, getTranslatedPost } from '../utils';

//Components
import { CMFlex } from '@chartmetric/chartmetric-design-system/components/CMFlex';
import { CMIcon } from '@chartmetric/chartmetric-design-system/components/CMIcon';
import { CMText } from '@chartmetric/chartmetric-design-system/components/CMText';
import AnnouncementComments from './AnnouncementComments';
import AnnouncementContentRenderer from './AnnouncementContentRenderer';
import AnnouncementReactions from './AnnouncementReactions';
import AnnouncementTag from './AnnouncementTag';
import AnnouncementPlanTag from './AnnouncementPlanTag';

//Types
import type { Announcement } from '../types';

interface AnnouncementCardProps {
  announcement: Announcement;
  onReactionChange?: () => void;
}

function AnnouncementCard({ announcement, onReactionChange }: AnnouncementCardProps) {
  const { t, i18n } = useTranslation('common', { keyPrefix: 'announcementPanel' });
  const { title, content } = getTranslatedPost(announcement, i18n.language);
  const dateLabel = formatAnnouncementDate(announcement.published_at);

  return (
    <CMFlex
      vertical
      gap="sm"
      p="2xl"
    >
      <CMFlex align="center" gap="sm">
        <CMText value={dateLabel} size="xs" color="tertiary" />
        {announcement.is_pinned && <CMIcon icon={farThumbtack} size="xs" color="activePrimary" title={t('pinned')} />}
      </CMFlex>
      <CMText value={title} variant="h5" />
      {(announcement.categories?.length > 0 || announcement.required_plan) && (
        <CMFlex align="center" gap="xs" wrap>
          {announcement.categories?.map((cat) => (
            <AnnouncementTag key={cat.name} category={cat} />
          ))}
          {announcement.required_plan && (
            <AnnouncementPlanTag plan={announcement.required_plan} />
          )}
        </CMFlex>
      )}
      {content && content.length > 0 && <AnnouncementContentRenderer content={content} />}
      <CMFlex vertical gap="md">
        <AnnouncementReactions
          announcementId={announcement.id}
          initialReactionCounts={announcement.reaction_counts}
          initialUserReaction={announcement.user_reaction}
          onReactionChange={onReactionChange}
        />
        <AnnouncementComments announcementId={announcement.id} initialComments={announcement.comments} />
      </CMFlex>
    </CMFlex>
  );
}

export default AnnouncementCard;
